import React, { useState } from "react";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Send } from "lucide-react";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { CONTACT_INFO, EVENT_TYPES, saveToStorage } from "../mock/mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing details", description: "Please add your name and email.", variant: "destructive" });
      return;
    }
    saveToStorage("atom_contacts", form);
    toast({ title: "Message sent! \uD83C\uDFB7", description: "We'll be in touch soon. Thanks for reaching out to A.T.O.M." });
    setForm({ name: "", email: "", phone: "", type: "", message: "" });
  };

  return (
    <div className="overflow-hidden">
      <section className="pt-40 pb-16 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">Get In Touch</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            Let's <span className="text-gold-gradient">Connect</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
            Have questions about booking A.T.O.M.? We'd love to hear from you.
          </p>
        </Reveal>
      </section>

      <section className="px-5 pb-24">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-8">
          {/* Info cards */}
          <div className="md:col-span-2 space-y-4">
            {[
              { icon: Mail, title: "Email Us", sub: "For booking inquiries and general questions", value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
              { icon: Phone, title: "Call Us", sub: "Call to book A.T.O.M.", value: CONTACT_INFO.phones.join("  \u2022  "), href: `tel:${CONTACT_INFO.phones[0]}` },
              { icon: MapPin, title: "Based In", sub: "Available for events nationwide", value: CONTACT_INFO.location, href: null },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="p-6 rounded-xl bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 transition-all duration-400">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-lg bg-[#e0b04c]/10 flex items-center justify-center"><c.icon className="text-[#e0b04c]" size={20} /></div>
                    <h3 className="font-serif-display text-xl text-[#f5f0e8]">{c.title}</h3>
                  </div>
                  <p className="text-[#8f8577] text-sm mb-1">{c.sub}</p>
                  {c.href ? <a href={c.href} className="text-[#e0b04c] hover:underline">{c.value}</a> : <p className="text-[#e0b04c]">{c.value}</p>}
                </div>
              </Reveal>
            ))}
            <Reveal delay={300}>
              <div className="p-6 rounded-xl bg-[#12100c] border border-white/5">
                <h3 className="font-serif-display text-xl text-[#f5f0e8] mb-4">Follow Us</h3>
                <div className="flex gap-3">
                  {[{ icon: Facebook, href: CONTACT_INFO.facebook }, { icon: Instagram, href: "#" }, { icon: Youtube, href: "#" }].map(({ icon: Icon, href }, i) => (
                    <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-[#e0b04c]/25 flex items-center justify-center text-[#c9bda9] hover:bg-[#e0b04c] hover:text-[#0c0a08] hover:border-[#e0b04c] transition-all duration-300"><Icon size={18} /></a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={150} className="md:col-span-3">
            <form onSubmit={submit} className="bg-[#12100c] border border-white/8 rounded-2xl p-8 space-y-5">
              <h2 className="font-serif-display text-2xl text-[#f5f0e8] mb-2">Send a Message</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Email Address *</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Phone Number</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c]" />
                </div>
                <div>
                  <Label className="text-[#c9bda9] mb-1.5 block text-sm">Type of Event</Label>
                  <Select value={form.type} onValueChange={(v) => update("type", v)}>
                    <SelectTrigger className="bg-[#1a1710] border-white/10 text-[#f5f0e8]"><SelectValue placeholder="Select event type" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1710] border-white/10 text-[#f5f0e8]">
                      {EVENT_TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[#c9bda9] mb-1.5 block text-sm">Your Message</Label>
                <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={5} placeholder="Tell us about your event or question..." className="bg-[#1a1710] border-white/10 text-[#f5f0e8] focus-visible:ring-[#e0b04c] resize-none" />
              </div>
              <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-3.5 rounded-md hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(224,176,76,0.35)] transition-all duration-300">
                <Send size={18} /> Send Message
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
